// addJoin — VQE-Edit edit-action: append a JOIN clause via SR-Edit's
// `add-join` op. Two entry points:
//   - `addJoinFromCandidate(candidate, leftAlias, ctx)` — primary path used
//     by the FK-driven Join chooser. Builds `targetText` from the candidate
//     and `onText` from the FK column pairs.
//   - `addJoinClause(clause, ctx)` — escape hatch for the Custom JOIN form
//     (user-typed kind + targetText + onText / usingColumns).
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove JOIN +
// edit ON predicate.

import type { JoinKind, QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import type { JoinCandidate } from '../../../../sql-resolver/types/schema';
import type { AddJoinClause } from '../../../../sql-resolver/types/structuralEdit';
import { commitEdit, type CommitEditCtx } from '../commitEdit';
import {
  baseRelationAliases,
  formatJoinOnText,
  formatJoinTargetText,
  joinColumnQualifiers,
} from './joinHelpers';
import { querySources } from '../../../../sql-resolver/scope/querySources';

export type AddJoinFromCandidateOptions = {
  /** JoinKind to emit; defaults to `inner`. */
  readonly kind?: JoinKind;
  /** If set, insert AFTER this 0-based JOIN-list index. Omit to append. */
  readonly afterIndex?: number;
};

export async function addJoinFromCandidate(
  candidate: JoinCandidate,
  leftAlias: string,
  ctx: CommitEditCtx,
  options: AddJoinFromCandidateOptions = {},
): Promise<void> {
  const structure = currentStructure(ctx);
  const engine = ctx.service.engineId;
  const snapshot = ctx.service.getSchemaSnapshot();
  // The statement's own tables, as it spells them: the ON's left side and the
  // bare-column qualifiers use their qualifiers (alias, else delimited name).
  // Resolved to the snapshot relations they name (schema included).
  const sources = querySources(structure, engine, { snapshot });
  // Qualifying bare columns / aliasing base tables stays a top-level-statement
  // rewrite (a drilled subquery body is joined as written, as before).
  const rewriteSources = ctx.statementInfo ? [] : sources;
  const clause: AddJoinClause = {
    kind: options.kind ?? 'inner',
    targetText: formatJoinTargetText(candidate, engine),
    onText: formatJoinOnText(candidate, leftAlias, engine, sources),
    // Qualify every bare column reference, in the same edit — the join's own
    // `o.` style then covers the whole statement (and nothing turns ambiguous).
    qualifyColumns: joinColumnQualifiers(rewriteSources, snapshot),
    // The join introduces an alias: alias the unaliased base table(s) too
    // (`customers c`). Both fields above name relations by their CURRENT
    // names; the planner rewrites them to whatever aliases it applies.
    aliasRelations: baseRelationAliases(ctx.statementInfo ? null : structure, candidate.suggestedAlias),
  };
  await commitEdit(
    {
      op: 'add-join',
      clause,
      ...(options.afterIndex !== undefined ? { afterIndex: options.afterIndex } : {}),
    },
    ctx,
  );
}

function currentStructure(ctx: CommitEditCtx): QueryStructure | null {
  try {
    return ctx.statementInfo
      ? ctx.service.extractQueryStructureForStatement(ctx.documentId, ctx.statementInfo)
      : ctx.service.extractQueryStructure(ctx.documentId, ctx.statementId);
  } catch {
    // No parsed statement to read — join without qualifying (the old behaviour).
    return null;
  }
}

export async function addJoinClause(
  clause: AddJoinClause,
  ctx: CommitEditCtx,
  options: { readonly afterIndex?: number } = {},
): Promise<void> {
  await commitEdit(
    {
      op: 'add-join',
      clause,
      ...(options.afterIndex !== undefined ? { afterIndex: options.afterIndex } : {}),
    },
    ctx,
  );
}
